import api from "core/api/api";
import { DB_PROVIDERS } from "app/components/appwizard/component-wizards/wizards/source/database-reader/configs/db-providers";
import _ from "underscore";
import { getSchemaTypeFromDBType } from "src/modules/flow-designer/vts/selectTables-utils";

export type Steps = Record<string, { step: number; name: string; isTransient: boolean; isLastStep: boolean }>;
export const tablesInputHelperText = `Enter tables as “<Schema>.<Table>”, separated by semicolons, or use wildcard (“%”).`;

const isOjetDownstreamInfoFilled = properties => {
    const connectionInfoFilled =
        properties.supportdownstreamcapture &&
        properties.primarydatabaseusername &&
        properties.primarydatabasepassword &&
        properties.primarydatabasehostname &&
        properties.serviceName &&
        properties.primaryDatabasePort;
    return connectionInfoFilled || false;
};

const getJDBCDownstreamURL = properties => {
    let type = properties.type;
    if (type === "OJet") {
        return (
            "jdbc:oracle:thin:@//" +
            properties.primaryDatabaseHostname +
            ":" +
            properties.primaryDatabasePort +
            "/" +
            properties.serviceName
        );
    }
};

function getSSlParams(adapter: string, properties: any, commonParams) {
    const useSSL = properties.usessl;
    adapter = adapter.toUpperCase();
    switch (adapter) {
        case "ORACLE":
            return {
                ...commonParams,
                ...(useSSL && {
                    sslConfigMap: {
                        trustStore: properties.truststore,
                        trustStoreType: properties.truststoretype,
                        trustStorePassword: properties.truststorepassword,
                        keyStore: properties.keystore || undefined,
                        keyStoreType: properties.keystoretype || undefined,
                        keyStorePassword: properties.keystorepassword || undefined
                    }
                })
            };
        case "OJET":
        case "OJETCDC":
            return {
                ...commonParams,
                ...(useSSL && {
                    sslConfigMap: {
                        serverCertificateDomain: properties.servercertificatedomain,
                        walletLocation: properties.walletlocation
                    }
                })
            };
        case "POSTGRESQL":
        case "YUGABYTE":
        case "POSTGRES":
            return {
                ...commonParams,
                ...(useSSL && {
                    sslConfigMap: {
                        useSSL: properties.usessl,
                        sslmode: properties.sslmode,
                        sslcert: properties.sslcert,
                        sslkeypk: properties.sslkeypk,
                        sslrootcert: properties.sslrootcert
                    }
                })
            };
        case "MSSQL":
        case "MSJET":
        case "MSSQL":
        case "SQLSERVER":
            return {
                ...commonParams,
                ...(useSSL && {
                    sslConfigMap: {
                        encrypt: true,
                        trustServerCertificate: properties.trustservercertificate || false,
                        integratedSecurity: properties.integratedsecurity || false,
                        trustStore: properties.truststore,
                        trustStorePassword: properties.truststorepassword,
                        hostNameInCertificate: properties.hostnameincertificate
                    }
                })
            };
        case "MYSQL":
        case "MARIADB":
        case "MARIADBXPAND":
            return {
                ...commonParams,
                ...(useSSL && {
                    sslConfigMap: {
                        useSSL: properties.usessl,
                        requireSSL: properties.requiressl,
                        verifyServerCertificate: properties.verifyservercertificate || false,
                        trustCertificateKeyStoreUrl: properties.trustcertificatekeystoreurl,
                        trustCertificateKeyStoreType: properties.trustCertificateKeyStoreType,
                        trustCertificateKeyStorePassword: properties.trustCertificateKeyStorePassword,
                        clientCertificateKeyStoreUrl: properties.clientCertificateKeyStoreUrl || undefined,
                        clientCertificateKeyStoreType: properties.clientCertificateKeyStoreType || undefined,
                        clientCertificateKeyStorePassword: properties.clientCertificateKeyStorePassword || undefined
                    }
                })
            };
        default:
            return null;
    }
}

async function getParams(adapter: string, properties: any): Promise<Object> {
    if (adapter === "SALESFORCE") {
        return {
            username: properties.username,
            password: properties.password,
            securityToken: properties.securitytoken,
            consumerKey: properties.consumerkey,
            consumerSecret: properties.consumersecret,
            accesstoken: properties.authtoken,
            apiEndPoint: properties.host
        };
    } else {
        const commonParams = {
            useConnectionProfile: properties.useconnectionprofile,
            connectionProfileName: properties.connectionprofilename,
            UserName: properties.username,
            Password: properties.password?.encrypted
                ? { encrypted: properties.password.encrypted }
                : properties.password,
            ConnectionURL: properties.connectionurl ?? properties.connectionUrl ,
            encrypted: properties.password?.hasOwnProperty("encrypted"),
            containerName: properties.containername || undefined,
            service: properties.service,
            ...(properties.sslconfig && { SSLConfig: properties.sslconfig })
        };

        return getSSlParams(adapter, properties, commonParams);
    }
}

const getDatabase = async (properties, type, isCDC, metaObjectId, adapterName) => {
    type = type === "POSTGRESQL" ? "POSTGRES" : type;
    const agentName = properties.agentname;
    const params = await getParams(type, properties);
    if (type === "OJet" && isOjetDownstreamInfoFilled(properties)) {
        const primaryDatabaseUsername = properties.primarydatabaseusername;
        const primaryDatabasePassword = properties.primarydatabasepassword;
        const downstreamConnectionURL = getJDBCDownstreamURL(properties);
        const downStreamParams = {
            UserName: primaryDatabaseUsername,
            Password: primaryDatabasePassword,
            ConnectionURL: downstreamConnectionURL
        };
        return api.WizardHelper_GetDBs("OJET", downStreamParams, agentName, metaObjectId, adapterName);
    }
    // All types (ORACLE, OJET, MYSQL, MSSQL, MARIADB, POSTGRES, YUGABYTE, etc.)
    let wizardType = type.toUpperCase();
    if (wizardType === "SQLSERVER" || wizardType === "MSJET") wizardType = "MSSQL";
    else if (wizardType === "OJET") wizardType = "OJET";
    if (wizardType === "POSTGRES" || wizardType === "MSSQL") {
        return api.WizardHelper_GetSchemas(wizardType, params, agentName, metaObjectId, adapterName);
    }
    return api.WizardHelper_GetDBs(wizardType, params, agentName, metaObjectId, adapterName);
};

const fetchTables = async (
    schemaName,
    properties,
    dbType,
    metaObjectId,
    adapterName,
    onSuccess,
    onFail
): Promise<String | null> => {
    let type = dbType.toLowerCase();
    let password = properties.password;
    const connectionURL = properties.connectionurl ?? properties.connectionUrl;
    let method: Promise<any> | null = null;
    const agentName = properties.agentname;
    const params = await getParams(type, properties);
    const isPasswordEncrypted = properties.password?.hasOwnProperty("encrypted");

    return new Promise((resolve, reject) => {
        switch (type) {
            case "cosmosdb": {
                password = properties.get("accesskey");
                const cosmosParams = { UserName: null, Password: password, ConnectionURL: connectionURL };
                method = api.WizardHelper_GetTables(
                    "COSMOS",
                    cosmosParams,
                    schemaName,
                    agentName,
                    metaObjectId,
                    adapterName
                );
                break;
            }
            case "salesforce": {
                method = api.WizardHelper_GetObjectsWithExtraParam(
                    "SALESFORCECDC",
                    {
                        useConnectionProfile: properties.useconnectionprofile,
                        connectionProfileName: properties.connectionprofilename,
                        username: properties.username,
                        password,
                        securityToken: properties.securitytoken,
                        consumerKey: properties.consumerkey,
                        consumerSecret: properties.consumersecret,
                        accesstoken: properties.authtoken,
                        apiEndPoint: properties.apiendpoint
                    },
                    schemaName,
                    agentName,
                    metaObjectId,
                    adapterName
                );
                break;
            }
            case "snowflake": {
                method = api.WizardHelper_GetTables(
                    "SNOWFLAKE",
                    {
                        useConnectionProfile: !!properties.connectionprofilename ? true : false,
                        connectionProfileName: properties.connectionprofilename,
                        username: properties.username,
                        password,
                        securityToken: properties.securitytoken,
                        consumerKey: properties.consumerkey,
                        consumerSecret: properties.consumersecret,
                        accesstoken: properties.authtoken,
                        apiEndPoint: properties.apiendpoint
                    },
                    `${schemaName}.%`,
                    agentName,
                    metaObjectId,
                    adapterName
                );
                break;
            }
            case "bigquery": {
                method = api.WizardHelper_GetTables(
                    "BIGQUERY",
                    {
                        useConnectionProfile: properties.useconnectionprofile,
                        connectionProfileName: properties.connectionprofilename,
                        username: properties.username,
                        password,
                        securityToken: properties.securitytoken,
                        consumerKey: properties.consumerkey,
                        consumerSecret: properties.consumersecret,
                        accesstoken: properties.authtoken,
                        connectionUrl: properties.connectionurl
                    },
                    `${schemaName}.%`,
                    agentName,
                    metaObjectId,
                    adapterName
                );
                break;
            }
            default: {
                let wizardType =
                    type === "mongo_cosmosdb" ? "MONGOCOSMOS" : type === "postgresql" ? "POSTGRES" : type.toUpperCase();
                if (wizardType === "SQLSERVER" || wizardType === "MSJET") wizardType = "MSSQL";
                method = api.WizardHelper_GetTables(
                    wizardType,
                    params,
                    `${schemaName}.%`,
                    agentName,
                    metaObjectId,
                    adapterName
                );
            }
        }

        if (method) {
            method.then(
                async data => {
                    if (data.result === true) {
                        let tables = data.message;
                        onSuccess(schemaName, tables);
                        resolve(data);
                    } else {
                        reject("Problem fetching Tables for " + schemaName);
                    }
                },
                () => {
                    onFail();
                    reject("Problem fetching Tables for " + schemaName);
                }
            );
        }
    });
};

export function getTableNameWithCorrectDots(tableName, properties, isCDC, provider) {
    const isPDB = properties.ispdb;
    const containerName = properties.containername;
    if ([DB_PROVIDERS.ORACLE.id, DB_PROVIDERS.OJET.id].includes(provider) && isCDC && isPDB && containerName) {
        let chunks = tableName.split(".");
        chunks.unshift(containerName);
        return chunks.join(".");
    }
    if (
        provider === DB_PROVIDERS.ORACLE.id ||
        provider === DB_PROVIDERS.OJET.id ||
        provider === DB_PROVIDERS.MYSQL.id ||
        provider === DB_PROVIDERS.MARIADB.id ||
        provider === DB_PROVIDERS.MARIADBXPAND.id ||
        provider === DB_PROVIDERS.POSTGRES.id ||
        // Spanner has no catalog, so getTables returns names with an empty leading
        // segment (e.g. ".schema.table"); dropping the first chunk yields "schema.table"
        // and avoids the empty-string identifier ("".schema.table) in the Tables property.
        provider === DB_PROVIDERS.SPANNER.id ||
        (provider === DB_PROVIDERS.SQLSERVER.id && isCDC) ||
        (provider === DB_PROVIDERS.MSJET.id && isCDC)
    ) {
        let chunks = tableName.split(".");
        if (chunks.length === 3) {
            chunks.shift();
        }
        return chunks.join(".");
    } else {
        return tableName;
    }
}

const getStepsName = (schemaType, entityType, isAppAdapter = false, dbType, isCDC, showPdbStep = false): Steps => {
    let stepsName = {};
    if (isAppAdapter) {
        const isSalesforceCDC = dbType === DB_PROVIDERS.SALESFORCE.id && isCDC;
        const currentSchemaType = getSchemaTypeFromDBType(dbType, schemaType, isCDC);
        stepsName = {
            SelectSchemas: {
                step: 0,
                name: `Select ${currentSchemaType}s`,
                isTransient: false,
                isLastStep: !isSalesforceCDC
            }
        };
        if (isSalesforceCDC) {
            stepsName = {
                ...stepsName,
                FetchSchemas: {
                    step: 1,
                    name: `Validate Channel`,
                    isTransient: true,
                    isLastStep: false
                },
                SelectTables: {
                    step: 2,
                    name: `Select Objects`,
                    isTransient: false,
                    isLastStep: true
                }
            };
        }
    } else {
        const pdbOffset = showPdbStep ? 1 : 0;
        stepsName = {
            ...(showPdbStep && {
                SelectPdb: {
                    step: 0,
                    name: "Select Pluggable Database",
                    isTransient: true,
                    isLastStep: false
                }
            }),
            SelectSchemas: {
                step: 0 + pdbOffset,
                name: `Select ${schemaType ? (schemaType === "Channel" ? schemaType : `${schemaType}s`) : "Schemas"} `,
                isTransient: false,
                isLastStep: false
            },
            FetchSchemas: {
                step: 1 + pdbOffset,
                name: `Fetch ${schemaType ? (schemaType === "Channel" ? schemaType : `${schemaType}s`) : "Schemas"} `,
                isTransient: true,
                isLastStep: false
            },
            SelectTables: {
                step: 2 + pdbOffset,
                name: `Select ${entityType ? `${entityType}s` : "Tables"} `,
                isTransient: false,
                isLastStep: true
            }
        };
    }
    return stepsName;
};

const isOracleOrOjet = (dbType: string): boolean => {
    return [DB_PROVIDERS.ORACLE.id, DB_PROVIDERS.OJET.id].includes(dbType);
};

const checkIsCDB = async (properties, dbType, metaObjectId?, adapterName?): Promise<boolean> => {
    const type = dbType.toUpperCase();
    const agentName = properties.agentname;
    const params = await getParams(type, properties);
    delete params["containerName"];
    try {
        const res = await api.WizardHelper_IsCDB(type, params, agentName, metaObjectId, adapterName);
        return res === "YES";
    } catch {
        return false;
    }
};

const fetchPdbList = async (properties, dbType, metaObjectId?, adapterName?): Promise<string[]> => {
    try {
        const type = dbType.toUpperCase();
        const agentName = properties.agentname;
        const params = await getParams(type, properties);
        delete params["containerName"];
        const pdbs = await api.WizardHelper_GetPDBs(type, params, agentName, metaObjectId, adapterName);
        return pdbs?.message || [];
    } catch {
        return [];
    }
};

const matchTextWithWildcard = (text: string, searchPattern: string, matchCase: boolean = false): boolean => {
    if (searchPattern.includes("%")) {
        const escapedText = searchPattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regexPattern = escapedText.replace(/%/g, ".*");
        const regex = new RegExp(`^${regexPattern}$`, matchCase ? "" : "i");
        return regex.test(text);
    } else {
        return matchCase ? text.includes(searchPattern) : text.toLowerCase().includes(searchPattern.toLowerCase());
    }
};

const isValidIdentifier = (value: string) => /^[a-zA-Z0-9_]*$/.test(value);

export {
    fetchTables,
    getDatabase,
    getStepsName,
    matchTextWithWildcard,
    isValidIdentifier,
    isOracleOrOjet,
    checkIsCDB,
    fetchPdbList
};
